import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Modal,
  Table,
  Badge,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FaFileAlt, FaInfoCircle } from "react-icons/fa";
import { getURLbyEndPointV2 } from "../../store/api";
import { getAuthUserlocal } from "../../store/services";
import axios from "axios";

const Room = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const meetingContainer = useRef(null);
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Sidebar state

  const urlParams = new URLSearchParams(window.location.search);
  const username = urlParams.get("username") || "AECCI Administrator";

  // Fetch dispute details by roomId
  const fetchDisputeDetails = async () => {
    try {
      setLoading(true);
      const user = getAuthUserlocal();
      if (!user) {
        console.error("No user found");
        setDisputeDetails(null);
        setLoading(false);
        return;
      }

      // Using getBindingDispute to fetch all disputes for the user
      const response = await axios.get(
        getURLbyEndPointV2("getBindingDispute") + user._id
      );

      if (response.data.status && Array.isArray(response.data.data)) {
        const allDisputes = response.data.data;
        // Find the dispute that contains the meeting with the current roomId
        const matchedDispute = allDisputes.find((d) =>
          d?.timeTable?.sections?.some((s) =>
            s?.meetings?.some((m) => m.meetingRoomId === roomId)
          )
        );
        setDisputeDetails(matchedDispute || null);
      } else {
        setDisputeDetails(null);
      }
    } catch (error) {
      console.error("Error fetching dispute details:", error);
      setDisputeDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Join Logic
  const [joinStatus, setJoinStatus] = useState("INIT"); // INIT, REQUESTED, ALLOWED, DENIED
  const [pollingInfo, setPollingInfo] = useState(null); // intervalId

  // Check initial status
  const checkInitialStatus = async () => {
    const user = getAuthUserlocal();
    if (!user) {
      // If external user without auth, maybe allow or block?
      // Assuming logged in users for now as per `getAuthUserlocal`.
      // If not logged in, maybe we use `username` from URL?
      // Let's assume user must be logged in or we use a temporary ID?
      // The previous code used `username` from URL param.
      // If user is guest, we need a stable ID.
      // For now, let's use username + some random string if no user._id
      return;
    }

    try {
      const res = await axios.post(getURLbyEndPointV2("checkJoinStatus"), {
        meetingRoomId: roomId,
        userId: user._id,
      });
      if (res.data.status && res.data.allowed) {
        setJoinStatus("ALLOWED");
      } else {
        // Not allowed yet. Automatically request?
        // Or show "Request to Join" button?
        // Let's automatically request for smoother UX.
        requestToJoin(user);
      }
    } catch (error) {
      console.error("Check status failed:", error);
    }
  };

  const requestToJoin = async (user) => {
    try {
      setJoinStatus("REQUESTED");
      const res = await axios.post(getURLbyEndPointV2("requestJoin"), {
        meetingRoomId: roomId,
        userId: user._id,
        name:
          `${user.firstName || ""} ${user.surName || ""}`.trim() || username,
        email: user.email,
        role: "User", // Determine role if possible
      });

      if (res.data.status) {
        if (res.data.allowed) {
          setJoinStatus("ALLOWED");
        } else {
          setJoinStatus("WAITING");
          startPolling(user._id);
        }
      }
    } catch (error) {
      console.error("Request join failed:", error);
    }
  };

  const startPolling = (userId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.post(getURLbyEndPointV2("checkJoinStatus"), {
          meetingRoomId: roomId,
          userId: userId,
        });
        if (res.data.status && res.data.allowed) {
          setJoinStatus("ALLOWED");
          clearInterval(interval);
        }
      } catch (e) {
        console.error(e);
      }
    }, 3000); // Check every 3 seconds
    setPollingInfo(interval);
  };

  useEffect(() => {
    // Clear interval on unmount
    return () => {
      if (pollingInfo) clearInterval(pollingInfo);
    };
  }, [pollingInfo]);

  useEffect(() => {
    fetchDisputeDetails();
    checkInitialStatus();
  }, [roomId]);

  // Initialize Zego Meeting ONLY if ALLOWED
  const myMeeting = async (element) => {
    const appID = 1871154113;
    const serverSecret = "7625a483cfcaa01920c9f3ceeaab8ff2";

    const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
      appID,
      serverSecret,
      roomId,
      Date.now().toString(),
      username
    );

    const zp = ZegoUIKitPrebuilt.create(kitToken);

    // joinRoom accepts callbacks like onLeaveRoom, onError
    zp.joinRoom({
      container: element,
      maxUsers: 10,
      scenario: { mode: ZegoUIKitPrebuilt.GroupCall },
      showPreJoinView: true,
      preJoinViewConfig: { title: "AECCI Arbitration Hearing" },
      branding: {
        headerTitle: "AECCIVirtual Arbitration Hearing",
        showHeader: true,
      },
      layout: "Grid",
      showRoomDetailsButton: true,
      showRoomTimer: true,
      showMoreButton: true,
      showMyCameraToggleButton: true,
      showMyMicrophoneToggleButton: true,
      showAudioVideoSettingsButton: true,
      showTurnOffRemoteCameraButton: true,
      showTurnOffRemoteMicrophoneButton: true,
      turnOnMicrophoneWhenJoining: false,
      turnOnCameraWhenJoining: true,
      useFrontFacingCamera: true,
      enableStereo: false,
      sharedLinks: [
        {
          name: "Join via link",
          url: window.location.href,
        },
      ],
      leaveConfirmDialogInfo: {
        title: "Leave Meeting",
        message: "Are you sure you want to leave the meeting?",
        cancelButtonName: "Cancel",
        confirmButtonName: "Yes, leave",
      },
      // important: use provided callbacks rather than .on()
      onLeaveRoom: () => {
        console.log("Meeting closed → redirecting...");
        navigate("/raise-your-dispute");
      },
      onError: (err) => {
        console.warn("Zego error:", err);
      },
    });
  };

  useEffect(() => {
    if (!meetingContainer.current || joinStatus !== "ALLOWED") return;
    const timeout = setTimeout(() => {
      try {
        myMeeting(meetingContainer.current);
      } catch (error) {
        console.error("Failed to start Zego meeting:", error);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [roomId, username, joinStatus]);

  // Helper functions
  const renderValue = (v) => (v || v === 0 ? v : "N/A");

  const formatDate = (d) => {
    if (!d) return "N/A";
    try {
      return new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const renderCurrency = (a) =>
    a || a === 0
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(a)
      : "N/A";

  const getStatusVariant = (s) => {
    switch (String(s || "").toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "document verified":
        return "info";
      default:
        return "secondary";
    }
  };

  const getDocuments = (data) => {
    if (!data) return [];

    const docKeys = [
      { key: "companyRegistrationDoc", label: "Company Registration Document" },
      { key: "agreementContract", label: "Agreement Contract" },
      { key: "arbitrationForm", label: "Arbitration Form" },
      {
        key: "correspondenceWithSupplier",
        label: "Correspondence with Supplier",
      },
      { key: "commercialInvoice", label: "Commercial Invoice" },
      { key: "inspectionReports", label: "Inspection Reports" },
      { key: "correspondenceWithAgent", label: "Correspondence with Agent" },
      {
        key: "correspondenceWithCarrier",
        label: "Correspondence with Carrier",
      },
      { key: "photographs", label: "Photographs" },
      { key: "legalActionDocuments", label: "Legal Action Documents" },
    ];

    // Single-field documents from the dispute data
    const singleFieldDocs = docKeys
      .filter((doc) => data[doc.key])
      .map((doc) => ({
        documentType: doc.label,
        uploadDate: data.uploadDate || data.createdAt || new Date(),
        status: "Uploaded",
        url: data[doc.key],
        source: "field",
      }));

    // Documents array from dispute data (if any)
    const arrayDocs = Array.isArray(data.documents)
      ? data.documents
          .filter((d) => d && d.url)
          .map((d) => ({
            documentType: d.documentType || d.label || "Document",
            uploadDate: d.uploadDate || d.createdAt || new Date(),
            status: d.status || "Uploaded",
            url: d.url,
            source: "documents_array",
          }))
      : [];

    // Additional documents from clientId and paymentSchedule
    const additionalDocs = [];

    // Membership Certificate from clientId
    if (data.clientId?.membershipCertificate) {
      additionalDocs.push({
        documentType: "Membership Certificate",
        uploadDate: data.createdAt || new Date(),
        status: "Uploaded",
        url: data.clientId.membershipCertificate,
        source: "clientId",
      });
    }

    // Payment Receipt from paymentSchedule
    if (data.paymentSchedule?.paymentDetails?.receipt) {
      additionalDocs.push({
        documentType: "Payment Receipt",
        uploadDate:
          data.paymentSchedule.paymentDetails.paymentCompleteDate || new Date(),
        status: "Uploaded",
        url: data.paymentSchedule.paymentDetails.receipt,
        source: "paymentSchedule",
      });
    }

    // Merge all documents
    const merged = [...singleFieldDocs, ...arrayDocs, ...additionalDocs];

    // Deduplicate by URL to avoid duplicates
    const seen = new Set();
    const unique = merged.filter((doc) => {
      if (!doc.url) return false;
      if (seen.has(doc.url)) return false;
      seen.add(doc.url);
      return true;
    });

    return unique;
  };

  const getTimeTableDocuments = (data) => {
    if (!data?.timeTable?.sections) return [];
    return data.timeTable.sections.flatMap((section) =>
      Object.entries(section.documents || {}).map(([docKey, url]) => ({
        documentType: `${section.sectionName} - ${docKey}`,
        uploadDate: section.completedAt || section.createdAt || data.createdAt,
        status: section.isCompleted ? "Uploaded" : "Pending",
        url,
      }))
    );
  };

  // Handle document view
  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0B1A33",
        color: "white",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#0D274A",
          borderBottom: "2px solid #1E3A5F",
          fontSize: "20px",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "1px",
          position: "relative",
          padding: "0 20px",
          color: "#fff",
        }}
      >
        {/* LEFT: Logo + Center Name */}
        <div
          style={{
            position: "absolute",
            left: 20,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <img
            src="https://www.aecci.org.in/wp-content/uploads/2020/10/AECCI-24.png"
            alt="AECCI Logo"
            style={{ height: "38px" }}
          />
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            INTERNATIONAL ARBITRATION CENTER
          </span>
        </div>

        {/* CENTER TITLE */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          AECCI-IAC VIRTUAL ARBITRATION HEARING
        </div>

        {/* RIGHT: Case ID */}
        <div
          style={{
            position: "absolute",
            right: 20,
            fontSize: "15px",
            fontWeight: "600",
            whiteSpace: "nowrap",
          }}
        >
          Case ID: {renderValue(disputeDetails?.caseId)}
        </div>
      </div>

      {/* MAIN */}
      <div style={{ display: "flex", flex: 1, position: "relative" }}>
        {/* Meeting area — parent must be position: relative for absolute children */}
        {/* Meeting area — parent must be position: relative for absolute children */}

        {joinStatus !== "ALLOWED" ? (
          <div
            style={{
              width: isSidebarOpen ? "80vw" : "100vw",
              height: "calc(100vh - 100px)",
              backgroundColor: "#1C2E4A",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              color: "white",
            }}
          >
            {joinStatus === "WAITING" || joinStatus === "REQUESTED" ? (
              <>
                <Spinner animation="grow" variant="light" />
                <h3 className="mt-3">Waiting for Host Approval...</h3>
                <p>Please stay on this screen.</p>
              </>
            ) : (
              <h3>Loading...</h3>
            )}
          </div>
        ) : (
          <div
            ref={meetingContainer}
            style={{
              width: isSidebarOpen ? "80vw" : "100vw",
              height: "calc(100vh - 100px)",
              backgroundColor: "#1C2E4A",
              position: "relative",
              transition: "width 0.3s ease-in-out", // Smooth width transition
            }}
          />
        )}

        {/* Recording button floating (relative to viewport) */}

        {/* Sidebar */}
        <div
          style={{
            width: isSidebarOpen ? "20vw" : "0",
            height: "calc(100vh - 100px)",
            overflowY: isSidebarOpen ? "auto" : "hidden",
            padding: isSidebarOpen ? "1rem" : "0",
            backgroundColor: "#fff",
            color: "#000",
            boxShadow: isSidebarOpen ? "-2px 0 5px rgba(0,0,0,0.1)" : "none",
            position: "absolute",
            right: 0,
            top: 0,
            transform: isSidebarOpen ? "translateX(0)" : "translateX(100%)",
            transition:
              "transform 0.3s ease-in-out, width 0.3s ease-in-out, padding 0.3s ease-in-out", // Smooth slide animation
            zIndex: 1000,
          }}
        >
          {isSidebarOpen && (
            <>
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "100%" }}
                >
                  <Spinner animation="border" variant="primary" />
                  <span className="ms-2">Loading dispute details...</span>
                </div>
              ) : !disputeDetails ? (
                <div className="text-center mt-5">
                  <h5>No Dispute Data Available</h5>
                </div>
              ) : (
                <>
                  {/* Claimant */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Claimant Information
                      </h6>
                      <p className="mb-1">
                        <strong>Name:</strong>{" "}
                        {renderValue(disputeDetails.claimantName)}
                      </p>
                      <p className="mb-1">
                        <strong>Address:</strong>{" "}
                        {renderValue(disputeDetails.address)}
                      </p>
                      <p className="mb-1">
                        <strong>City:</strong>{" "}
                        {renderValue(disputeDetails.city)}
                      </p>
                      <p className="mb-1">
                        <strong>Zip Code:</strong>{" "}
                        {renderValue(disputeDetails.zipCode)}
                      </p>
                      <p className="mb-1">
                        <strong>Country:</strong>{" "}
                        {renderValue(disputeDetails.country)}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong>{" "}
                        {renderValue(disputeDetails.phone)}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong>{" "}
                        {renderValue(disputeDetails.email)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Business */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Business Details
                      </h6>
                      <p className="mb-1">
                        <strong>Nature of Business:</strong>{" "}
                        {renderValue(disputeDetails.natureOfBusiness)}
                      </p>
                      <p className="mb-1">
                        <strong>Industry:</strong>{" "}
                        {renderValue(disputeDetails.industry)}
                      </p>
                      <p className="mb-1">
                        <strong>AECCI Registration No:</strong>{" "}
                        {renderValue(disputeDetails.aecciRegNo)}
                      </p>
                      <p className="mb-1">
                        <strong>Member Type:</strong>{" "}
                        {renderValue(disputeDetails.memberType)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Respondent */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Respondent Information
                      </h6>
                      <p className="mb-1">
                        <strong>Company Name:</strong>{" "}
                        {renderValue(disputeDetails.respondentCompanyName)}
                      </p>
                      <p className="mb-1">
                        <strong>Name:</strong>{" "}
                        {renderValue(
                          disputeDetails.respondentRepresentativeName
                        )}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong>{" "}
                        {renderValue(disputeDetails.respondentEmail)}
                      </p>
                      <p className="mb-1">
                        <strong>Address:</strong>{" "}
                        {renderValue(disputeDetails.respondentAddress)}
                      </p>
                      <p className="mb-1">
                        <strong>State:</strong>{" "}
                        {renderValue(disputeDetails.respondentState)}
                      </p>
                      <p className="mb-1">
                        <strong>Country:</strong>{" "}
                        {renderValue(disputeDetails.respondentCountry)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Dispute */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Dispute Details
                      </h6>
                      <p className="mb-1">
                        <strong>Application No:</strong>{" "}
                        {renderValue(disputeDetails.appNo)}
                      </p>
                      <p className="mb-1">
                        <strong>Place:</strong>{" "}
                        {renderValue(disputeDetails.place)}
                      </p>
                      <p className="mb-1">
                        <strong>Product:</strong>{" "}
                        {renderValue(disputeDetails.product)}
                      </p>
                      <p className="mb-1">
                        <strong>Application Date:</strong>{" "}
                        {formatDate(disputeDetails.createdAt)}
                      </p>
                      <p className="mb-1">
                        <strong>Date:</strong> {formatDate(disputeDetails.date)}
                      </p>
                      <p className="mb-1">
                        <strong>Agreement Start:</strong>{" "}
                        {formatDate(disputeDetails.agreementStartDate)}
                      </p>
                      <p className="mb-1">
                        <strong>Dispute Start:</strong>{" "}
                        {formatDate(disputeDetails.disputeStartDate)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Financial */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Financial Details
                      </h6>
                      <p className="mb-1">
                        <strong>Invoice Amount:</strong>{" "}
                        {renderCurrency(disputeDetails.commercialInvoiceAmount)}
                      </p>
                      <p className="mb-1">
                        <strong>Amount of Activity:</strong>{" "}
                        {renderCurrency(disputeDetails.amountOfActivity)}
                      </p>
                      <p className="mb-1">
                        <strong>Quantum of Claim:</strong>{" "}
                        {renderCurrency(disputeDetails.quantumOfClaim)}
                      </p>
                      <p className="mb-1">
                        <strong>Amount in Dispute:</strong>{" "}
                        {renderCurrency(disputeDetails.amountInDispute)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Description */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Dispute Description
                      </h6>
                      <p className="mb-1">
                        {renderValue(disputeDetails.disputeDescription)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Uploaded Documents */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Uploaded Documents
                      </h6>
                      {getDocuments(disputeDetails).length > 0 ? (
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Document Type</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDocuments(disputeDetails).map((doc, index) => (
                              <tr key={index}>
                                <td className="py-1">{doc.documentType}</td>
                                <td className="py-1">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="py-0 px-2"
                                    style={{ fontSize: "12px" }}
                                    onClick={() => handleViewDocument(doc)}
                                    disabled={!doc.url}
                                  >
                                    <FaFileAlt className="me-1" /> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="mb-0">No documents available</p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Time Table Documents */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Time Table Documents
                      </h6>
                      {getTimeTableDocuments(disputeDetails).length > 0 ? (
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Document Type</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getTimeTableDocuments(disputeDetails).map(
                              (doc, index) => (
                                <tr key={index}>
                                  <td className="py-1">{doc.documentType}</td>
                                  <td className="py-1">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="py-0 px-2"
                                      style={{ fontSize: "12px" }}
                                      onClick={() => handleViewDocument(doc)}
                                      disabled={!doc.url}
                                    >
                                      <FaFileAlt className="me-1" /> View
                                    </Button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="mb-0">
                          No time table documents available
                        </p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Registered Respondent */}
                  {disputeDetails.respondentId && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Registered Respondent Details
                        </h6>
                        <p className="mb-1">
                          <strong>Company:</strong>{" "}
                          {renderValue(disputeDetails.respondentId.companyName)}
                        </p>
                        <p className="mb-1">
                          <strong>Name:</strong>{" "}
                          {renderValue(
                            `${disputeDetails.respondentId.firstName} ${disputeDetails.respondentId.surName}`
                          )}
                        </p>
                        <p className="mb-1">
                          <strong>Email:</strong>{" "}
                          {renderValue(disputeDetails.respondentId.email)}
                        </p>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Appointed Arbitrators */}
                  {disputeDetails.appointedArbitratorIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Appointed Arbitrators
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.appointedArbitratorIds.map(
                              (arb, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${arb.firstName} ${arb.surName}`
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Claimant Other Parties */}
                  {disputeDetails.claimantOtherPartyIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Claimant Other Parties
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>doc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.claimantOtherPartyIds.map(
                              (party, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${party.firstName} ${party.lastName}`
                                    )}
                                    {party.isRegistered ? (
                                      <Badge bg="success" className="ms-2">
                                        Registered
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" className="ms-2">
                                        Non-Registered
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="py-1">
                                    {renderValue(party.partyType)}
                                  </td>
                                  <td className="py-1">
                                    {party.claimantStatement ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ fontSize: "12px" }}
                                        onClick={() =>
                                          handleViewDocument({
                                            documentType: "Claimant Statement",
                                            url: party.claimantStatement,
                                          })
                                        }
                                      >
                                        <FaFileAlt className="me-1" /> View
                                      </Button>
                                    ) : (
                                      "None"
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Respondent Other Parties */}
                  {disputeDetails.respondentOtherPartyIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Respondent Other Parties
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>doc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.respondentOtherPartyIds.map(
                              (party, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${party.firstName} ${party.lastName}`
                                    )}
                                    {party.isRegistered ? (
                                      <Badge bg="success" className="ms-2">
                                        Registered
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" className="ms-2">
                                        Non-Registered
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="py-1">
                                    {renderValue(party.partyType)}
                                  </td>
                                  <td className="py-1">
                                    {party.claimantStatement ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ fontSize: "12px" }}
                                        onClick={() =>
                                          handleViewDocument({
                                            documentType: "Claimant Statement",
                                            url: party.claimantStatement,
                                          })
                                        }
                                      >
                                        <FaFileAlt className="me-1" /> View
                                      </Button>
                                    ) : (
                                      "None"
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Status */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">Status</h6>
                      <p className="mb-1">
                        <strong>Status:</strong>{" "}
                        <Badge bg={getStatusVariant(disputeDetails.status)}>
                          {renderValue(disputeDetails.status)}
                        </Badge>
                      </p>
                      <p className="mb-1">
                        <strong>Approval:</strong>{" "}
                        {renderValue(disputeDetails.isApproved)}
                      </p>
                    </Card.Body>
                  </Card>
                </>
              )}
            </>
          )}
        </div>

        {/* Sidebar Toggle Button */}
        <Button
          variant="light"
          style={{
            position: "absolute",
            right: isSidebarOpen ? "20vw" : "0",
            top: "10px",
            zIndex: 1001,
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            transition: "right 0.3s ease-in-out",
          }}
          onClick={toggleSidebar}
        >
          {isSidebarOpen ? "<" : ">"}
        </Button>
      </div>

      {/* FOOTER */}
      <div
        style={{
          height: "40px",
          backgroundColor: "#0D274A",
          color: "rgba(255,255,255,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontSize: "13px",
          borderTop: "2px solid #1E3A5F",
          position: "relative",
        }}
      >
        <span>
          Hearing conducted under AECCI–IAC Arbitration Rules (2023 Edition)
        </span>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip
                id="rules-tooltip"
                className="wide-tooltip"
                style={{ maxWidth: "1200px" }}
              >
                <div
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    fontSize: "12px",
                  }}
                >
                  <p className="mb-2">
                    As per discussion had recently, we look forward to welcoming
                    you to the upcoming Virtual Mock Arbitration session hosted
                    by AECCI-International Arbitration Centre. These mock
                    hearings are designed to offer a real experience of
                    arbitration proceedings.
                  </p>
                  <p className="mb-2">
                    To ensure the session runs smoothly and remains beneficial
                    for all, we kindly request all the participants to take note
                    of the following Do's and Don'ts:
                  </p>

                  <strong>Do's</strong>
                  <p className="mb-1">
                    Please treat the session as real arbitration, maintaining
                    appropriate structure, language, and formalities.
                  </p>
                  <ol
                    style={{
                      paddingLeft: "20px",
                      marginBottom: "10px",
                      listStyleType: "decimal",
                    }}
                  >
                    <li>
                      Join the virtual room at least 15 minutes before the
                      scheduled time to resolve any technical issues.
                    </li>
                    <li>
                      Dress in professional or semi-formal attire as you would
                      for a physical hearing.
                    </li>
                    <li>
                      Keep microphones muted when not speaking, and ensure
                      minimal background noise.
                    </li>
                    <li>
                      Ensure a reliable internet connection and choose a quiet
                      location for the duration of the session.
                    </li>
                    <li>
                      Keep case documents and any required materials ready and
                      easily accessible during the proceedings.
                    </li>
                    <li>
                      Maintain decorum, including respectful communication and
                      clarity while presenting arguments.
                    </li>
                  </ol>
                  <p className="mb-2">
                    <em>
                      Note: The session will be recorded for internal learning
                      and review purposes. Your participation constitutes
                      consent to this.
                    </em>
                  </p>

                  <strong>Don'ts</strong>
                  <p className="mb-1">
                    We deeply appreciate the wisdom and experience that senior
                    arbitrators and practitioners bring to the table. However,
                    to maintain the structure and realism of the mock hearings,
                    we respectfully request:
                  </p>
                  <ol
                    style={{
                      paddingLeft: "20px",
                      marginBottom: "10px",
                      listStyleType: "decimal",
                    }}
                  >
                    <li>
                      Do not multitask or engage in unrelated activities while
                      the session is in progress.
                    </li>
                    <li>
                      Avoid speaking over others; wait for the presiding
                      arbitrator to acknowledge you.
                    </li>
                    <li>
                      Do not share the meeting link with unauthorized persons.
                    </li>
                    <li>
                      Do not leave the virtual session abruptly without
                      informing the AECCI-administrator or tribunal.
                    </li>
                    <li>
                      Do not disregard time limits set for submissions and
                      arguments.
                    </li>
                    <li>Do not turn off your camera during the session.</li>
                  </ol>

                  <p className="mb-2">
                    For technical or procedural questions, please feel free to
                    contact us.
                  </p>
                  <p className="mb-0">
                    We thank you for your cooperation and look forward to an
                    engaging and productive session for all.
                  </p>
                </div>
              </Tooltip>
            }
          >
            <span
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span>Do's and Don'ts</span>
              <FaInfoCircle size={16} />
            </span>
          </OverlayTrigger>
        </div>
        <span>All communications encrypted and recorded for record.</span>
      </div>
      <style>
        {`
          .wide-tooltip .tooltip-inner {
            max-width: 600px !important;
            width: 600px !important;
            text-align: left;
          }
        `}
      </style>

      {/* Document Modal */}
      <Modal
        show={showDocumentModal}
        onHide={handleCloseDocumentModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Document Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument ? (
            <div>
              <h6>{selectedDocument.documentType}</h6>

              {selectedDocument.url ? (
                <iframe
                  src={selectedDocument.url}
                  style={{ width: "100%", height: "500px", border: "none" }}
                  title={selectedDocument.documentType}
                />
              ) : (
                <p>Document not available</p>
              )}
            </div>
          ) : (
            <p>No document selected</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDocumentModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Room;
